import React, { useState, useEffect } from 'react';
import {
    Users, Shield, Search, Server, RefreshCw, X, Layers,
    Hash, Database, Info, Link2, Wallet, CreditCard, ScanLine
} from 'lucide-react';
import PhoneOsintPanel from '../components/mulehunter/PhoneOsintPanel';
import api from '../services/api';
import { toast } from 'sonner';

/**
 * Syndicate clustering — every figure on this page is DERIVED.
 *
 * A cluster is a connected component of identifiers that were observed together
 * at the same sighting (one Telegram message / P2P advertiser / Instagram run),
 * transitively closed. There is no hand-labelled syndicate table and no seeded
 * demo rows, so the counts shown here are defensible: each one traces back to
 * rows in mh_identifiers / mh_observations.
 */

interface Cluster {
    cluster_id: string;
    label: string;
    label_type: string;
    members: number;
    observations: number;
    shared_sightings: number;
    by_type: Record<string, number>;
    source_classes: string[];
    source_class_count: number;
    payment_rails: number;
    wallets: number;
    risk: Record<string, number>;
    first_seen: string | null;
    last_seen: string | null;
}

interface ClusterTotals {
    clusters: number;
    clustered_identifiers: number;
    multi_surface_clusters: number;
    largest_cluster: number;
}

interface ClusterMethod {
    basis: string;
    max_ids_per_sighting: number;
    min_cluster_size: number;
}

interface ClusterResponse {
    clusters: Cluster[];
    totals: ClusterTotals;
    method: ClusterMethod;
}

interface Sighting {
    source_class: string;
    source_table: string;
    source_ref: string;
    context_snippet: string | null;
    observed_at: string | null;
}

interface MemberIdentifier {
    id: number;
    id_type: string;
    value: string;
    chain: string | null;
    occurrences: number;
    source_count: number;
    sources: string[];
    entity_name: string | null;
    entity_category: string | null;
    risk_hint: string | null;
    first_seen: string | null;
    last_seen: string | null;
    recent_sightings: Sighting[];
}

interface MemberResponse {
    cluster_id: string;
    label: string;
    label_type: string;
    members: number;
    shared_sightings: number;
    source_classes: string[];
    by_type: Record<string, number>;
    identifiers: MemberIdentifier[];
}

interface LookupMatch {
    id: number;
    id_type: string;
    value: string;
    occurrences: number;
    source_count: number;
    sources: string[];
    cluster_id: string | null;
    cluster_label: string | null;
    cluster_members: number;
    clustered: boolean;
}

const SOURCE_STYLES: Record<string, string> = {
    telegram: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/30',
    p2p: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30',
    instagram: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/30',
    flowguard: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
    feed: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30',
};

const sourceBadge = (s: string) =>
    SOURCE_STYLES[s] || 'bg-muted text-muted-foreground border-border';

const StatCard: React.FC<{
    label: string;
    value: string | number;
    hint: string;
    icon: React.ReactNode;
}> = ({ label, value, hint, icon }) => (
    <div className="p-4 rounded-xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </span>
            {icon}
        </div>
        <div className="mt-2 text-2xl font-bold text-foreground">
            {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </div>
);

export const SyndicateOperations: React.FC = () => {
    const [clusters, setClusters] = useState<Cluster[]>([]);
    const [totals, setTotals] = useState<ClusterTotals | null>(null);
    const [method, setMethod] = useState<ClusterMethod | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [filterQuery, setFilterQuery] = useState<string>('');

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searching, setSearching] = useState<boolean>(false);
    const [lookupMatches, setLookupMatches] = useState<LookupMatch[] | null>(null);

    const [detail, setDetail] = useState<MemberResponse | null>(null);
    const [osintOpen, setOsintOpen] = useState<Set<number>>(new Set());
    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

    const fetchClusters = async () => {
        setLoading(true);
        try {
            const res = await api.get('/mulehunter/syndicates?limit=100');
            const data: ClusterResponse = res.data;
            setClusters(data?.clusters || []);
            setTotals(data?.totals || null);
            setMethod(data?.method || null);
        } catch (err) {
            console.error('Failed to load syndicate clusters:', err);
            toast.error('Could not compute clusters from the harvest index.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    const handleLookup = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const q = searchQuery.trim();
        if (!q) {
            setLookupMatches(null);
            return;
        }
        setSearching(true);
        try {
            const res = await api.get(
                `/mulehunter/syndicates/lookup?q=${encodeURIComponent(q)}`
            );
            const matches: LookupMatch[] = res.data?.matches || [];
            setLookupMatches(matches);
            if (matches.length === 0) {
                toast.info(`"${q}" is not in the harvest index.`);
            } else {
                const inCluster = matches.filter((m) => m.clustered).length;
                toast.success(
                    `${matches.length} index match(es) — ${inCluster} inside a cluster.`
                );
            }
        } catch (err) {
            console.error('Attribution lookup failed:', err);
            toast.error('Attribution lookup failed.');
        } finally {
            setSearching(false);
        }
    };

    const openCluster = async (clusterId: string) => {
        setLoadingDetail(true);
        setDetail(null);
        try {
            const res = await api.get(
                `/mulehunter/syndicates/${encodeURIComponent(clusterId)}/members?limit=300`
            );
            setDetail(res.data);
        } catch (err) {
            console.error('Failed to load cluster members:', err);
            toast.error(`Could not load members for ${clusterId}.`);
        } finally {
            setLoadingDetail(false);
        }
    };

    const filtered = clusters.filter((c) => {
        const q = filterQuery.trim().toLowerCase();
        if (!q) return true;
        return (
            c.label.toLowerCase().includes(q) ||
            c.cluster_id.toLowerCase().includes(q) ||
            Object.keys(c.by_type).some((t) => t.includes(q)) ||
            c.source_classes.some((s) => s.includes(q))
        );
    });

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 border-b border-border pb-5">
                <div className="flex items-start gap-2">
                    <span className="p-2 bg-primary/10 text-primary rounded-lg">
                        <Users className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-foreground">
                            Syndicate Operations
                        </h1>
                        <p className="text-sm text-muted-foreground mt-0.5 max-w-3xl">
                            Identifiers that keep turning up together across Telegram, P2P and
                            Instagram, grouped into operations. Every figure below is computed
                            from the harvest index — nothing is pre-labelled.
                        </p>
                    </div>
                </div>

                <button
                    onClick={fetchClusters}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border border-border bg-card hover:bg-accent text-foreground transition-colors disabled:opacity-50 shrink-0"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Recompute
                </button>
            </div>

            {/* Derived metrics — no hardcoded figures */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    label="Clusters"
                    value={totals?.clusters ?? (loading ? '—' : 0)}
                    hint="Connected components of co-seen identifiers"
                    icon={<Layers className="w-4 h-4 text-primary" />}
                />
                <StatCard
                    label="Clustered identifiers"
                    value={totals?.clustered_identifiers ?? (loading ? '—' : 0)}
                    hint="Index rows belonging to an operation"
                    icon={<Database className="w-4 h-4 text-sky-500" />}
                />
                <StatCard
                    label="Multi-surface clusters"
                    value={totals?.multi_surface_clusters ?? (loading ? '—' : 0)}
                    hint="Corroborated on 2+ source classes"
                    icon={<Link2 className="w-4 h-4 text-violet-500" />}
                />
                <StatCard
                    label="Largest cluster"
                    value={totals?.largest_cluster ?? (loading ? '—' : 0)}
                    hint="Identifiers in the biggest component"
                    icon={<Shield className="w-4 h-4 text-amber-500" />}
                />
            </div>

            {/* Method disclosure — so the number can be defended, not just shown */}
            {method && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-border bg-muted/40 text-xs text-muted-foreground">
                    <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                    <span>
                        <span className="font-semibold text-foreground">How these are built: </span>
                        {method.basis}. Two identifiers are linked when they appear at the same
                        sighting; links are closed transitively. Sightings naming more than{' '}
                        {method.max_ids_per_sighting} identifiers are excluded as broadcast dumps,
                        and a cluster needs at least {method.min_cluster_size} members.
                    </span>
                </div>
            )}

            {/* Attribution lookup */}
            <div className="p-4 rounded-xl border border-border bg-card shadow-sm space-y-3">
                <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-primary" />
                    <h2 className="text-sm font-semibold text-foreground">
                        Attribute an identifier
                    </h2>
                </div>
                <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-2">
                    <input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Phone, UPI, wallet, handle or domain…"
                        className="flex-1 px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <button
                        type="submit"
                        disabled={searching}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        {searching ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                            <Search className="w-4 h-4" />
                        )}
                        Look up
                    </button>
                </form>

                {lookupMatches && lookupMatches.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                                    <th className="py-2 pr-3 font-semibold">Type</th>
                                    <th className="py-2 pr-3 font-semibold">Value</th>
                                    <th className="py-2 pr-3 font-semibold">Seen</th>
                                    <th className="py-2 pr-3 font-semibold">Sources</th>
                                    <th className="py-2 pr-3 font-semibold">Cluster</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lookupMatches.map((m) => (
                                    <tr key={m.id} className="border-b border-border/60">
                                        <td className="py-2 pr-3 text-muted-foreground">{m.id_type}</td>
                                        <td className="py-2 pr-3 font-mono text-xs break-all text-foreground">
                                            {m.value}
                                        </td>
                                        <td className="py-2 pr-3 text-muted-foreground">
                                            {m.occurrences}×
                                        </td>
                                        <td className="py-2 pr-3">
                                            <div className="flex flex-wrap gap-1">
                                                {m.sources.map((s) => (
                                                    <span
                                                        key={s}
                                                        className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${sourceBadge(s)}`}
                                                    >
                                                        {s}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="py-2 pr-3">
                                            {m.clustered && m.cluster_id ? (
                                                <button
                                                    onClick={() => openCluster(m.cluster_id as string)}
                                                    className="text-primary hover:underline font-medium"
                                                >
                                                    {m.cluster_label} ({m.cluster_members})
                                                </button>
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    not clustered
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {lookupMatches && lookupMatches.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                        No match in the harvest index.
                    </p>
                )}
            </div>

            {/* Cluster list */}
            <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold text-foreground">
                        Clusters{' '}
                        <span className="text-muted-foreground font-normal">
                            ({filtered.length}
                            {totals && filtered.length !== totals.clusters
                                ? ` of ${totals.clusters}`
                                : ''}
                            )
                        </span>
                    </h2>
                    <input
                        value={filterQuery}
                        onChange={(e) => setFilterQuery(e.target.value)}
                        placeholder="Filter by label, id type or source…"
                        className="px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 sm:w-72"
                    />
                </div>

                {loading && (
                    <div className="p-8 text-center text-sm text-muted-foreground border border-border rounded-xl bg-card">
                        <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                        Computing clusters from the harvest index…
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div className="p-8 text-center border border-border rounded-xl bg-card">
                        <Database className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium text-foreground">No clusters yet</p>
                        <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                            Clusters appear once the index holds sightings where two or more
                            identifiers were seen together. Run a harvest or a backfill to
                            populate co-occurrence.
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {filtered.map((c) => (
                        <button
                            key={c.cluster_id}
                            onClick={() => openCluster(c.cluster_id)}
                            className="text-left p-4 rounded-xl border border-border bg-card shadow-sm hover:border-primary/50 hover:bg-accent/40 transition-colors"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                            {c.label_type}
                                        </span>
                                        <span className="text-xs text-muted-foreground font-mono">
                                            {c.cluster_id}
                                        </span>
                                    </div>
                                    <p className="mt-1 font-mono text-sm font-medium text-foreground break-all">
                                        {c.label}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-xl font-bold text-foreground">
                                        {c.members}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                        members
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1">
                                    <Link2 className="w-3.5 h-3.5" />
                                    {c.shared_sightings} shared sighting
                                    {c.shared_sightings === 1 ? '' : 's'}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <Hash className="w-3.5 h-3.5" />
                                    {c.observations.toLocaleString()} observations
                                </span>
                                {c.wallets > 0 && (
                                    <span className="inline-flex items-center gap-1">
                                        <Wallet className="w-3.5 h-3.5" />
                                        {c.wallets} wallet{c.wallets === 1 ? '' : 's'}
                                    </span>
                                )}
                                {c.payment_rails > 0 && (
                                    <span className="inline-flex items-center gap-1">
                                        <CreditCard className="w-3.5 h-3.5" />
                                        {c.payment_rails} payment rail
                                        {c.payment_rails === 1 ? '' : 's'}
                                    </span>
                                )}
                            </div>

                            <div className="mt-3 flex flex-wrap gap-1">
                                {c.source_classes.map((s) => (
                                    <span
                                        key={s}
                                        className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${sourceBadge(s)}`}
                                    >
                                        {s}
                                    </span>
                                ))}
                                {Object.entries(c.by_type).map(([t, n]) => (
                                    <span
                                        key={t}
                                        className="px-1.5 py-0.5 rounded border border-border bg-muted/60 text-[10px] font-medium text-muted-foreground"
                                    >
                                        {t} {n}
                                    </span>
                                ))}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Cluster detail */}
            {(detail || loadingDetail) && (
                <div
                    className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
                    onClick={() => {
                        setDetail(null);
                        setLoadingDetail(false);
                    }}
                >
                    <div
                        className="w-full max-w-4xl my-8 rounded-xl border border-border bg-card shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
                            <div className="min-w-0">
                                <h3 className="font-semibold text-foreground">
                                    {loadingDetail ? 'Loading cluster…' : detail?.label}
                                </h3>
                                {detail && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {detail.cluster_id} · {detail.members} members ·{' '}
                                        {detail.shared_sightings} shared sighting
                                        {detail.shared_sightings === 1 ? '' : 's'} ·{' '}
                                        {detail.source_classes.join(', ')}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => {
                                    setDetail(null);
                                    setLoadingDetail(false);
                                }}
                                className="p-1 rounded hover:bg-accent text-muted-foreground shrink-0"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="p-4">
                            {loadingDetail && (
                                <div className="py-8 text-center text-sm text-muted-foreground">
                                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                                    Loading members…
                                </div>
                            )}

                            {detail && (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                                                <th className="py-2 pr-3 font-semibold">Type</th>
                                                <th className="py-2 pr-3 font-semibold">Value</th>
                                                <th className="py-2 pr-3 font-semibold">Seen</th>
                                                <th className="py-2 pr-3 font-semibold">Sources</th>
                                                <th className="py-2 pr-3 font-semibold">Attribution</th>
                                                <th className="py-2 font-semibold">Last sighting</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.identifiers.map((m) => (
                                                <React.Fragment key={m.id}>
                                                <tr className="border-b border-border/60 align-top">
                                                    <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                                                        {m.id_type}
                                                        {m.chain ? ` · ${m.chain}` : ''}
                                                    </td>
                                                    <td className="py-2 pr-3 font-mono text-xs break-all text-foreground max-w-[220px]">
                                                        {m.value}
                                                        {m.id_type === 'phone' && (
                                                            <button
                                                                type="button"
                                                                aria-label="Phone OSINT"
                                                                title="Phone OSINT (cached; live check is opt-in)"
                                                                onClick={() => setOsintOpen((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
                                                                    return next;
                                                                })}
                                                                className="ml-1.5 inline-flex align-middle p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent/40"
                                                            >
                                                                <ScanLine className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                                                        {m.occurrences}×
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <div className="flex flex-wrap gap-1">
                                                            {m.sources.map((s) => (
                                                                <span
                                                                    key={s}
                                                                    className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${sourceBadge(s)}`}
                                                                >
                                                                    {s}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="py-2 pr-3 text-xs text-muted-foreground">
                                                        {m.entity_name || '—'}
                                                        {m.risk_hint ? ` · ${m.risk_hint}` : ''}
                                                    </td>
                                                    <td className="py-2 text-xs text-muted-foreground">
                                                        {m.recent_sightings[0] ? (
                                                            <span title={m.recent_sightings[0].context_snippet || ''}>
                                                                {m.recent_sightings[0].source_class} ·{' '}
                                                                <span className="font-mono">
                                                                    {m.recent_sightings[0].source_ref}
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </td>
                                                </tr>
                                                {m.id_type === 'phone' && osintOpen.has(m.id) && (
                                                    <tr className="border-b border-border/60">
                                                        <td colSpan={6} className="py-2">
                                                            <PhoneOsintPanel phone={m.value} compact />
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SyndicateOperations;
